import React from 'react';
import { Globe, TrendingUp, ShieldCheck, Users, Bell, Moon, Lock, Server, Puzzle, Brain } from 'lucide-react';
import AnimatedGradientText from './AnimatedGradientText';
import { useLang } from './LanguageProvider';
import translations from '../translations';

const featureIcons = [Globe, TrendingUp, ShieldCheck, Users, Bell, Moon, Lock, Server, Puzzle, Brain, Server];
const featureColors = ['#06b6d4', '#10b981', '#8b5cf6', '#3b82f6', '#f59e0b', '#06b6d4', '#10b981', '#3b82f6', '#f97316', '#a855f7', '#10b981'];

export default function Features() {
  const { lang } = useLang();
  const t = translations[lang].features;

  return (
    <section data-testid="features-section" id="features" className="py-20 sm:py-24 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-0 w-80 h-80 bg-cyan-500/[0.03] rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-0 w-80 h-80 bg-purple-500/[0.03] rounded-full blur-3xl" />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-200 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-cyan-500">{t.badge}</span>
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4"><AnimatedGradientText>{t.title}</AnimatedGradientText></h2>
          <p className="text-base dark:text-white/50 text-slate-500 max-w-lg mx-auto">{t.desc}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {t.items.slice(0, 8).map((feat, i) => {
            const Icon = featureIcons[i];
            const color = featureColors[i];
            return (
              <div key={i} data-testid={`feature-hex-tile-${i}`} className="relative group overflow-hidden rounded-2xl p-5 glow-card" style={{ '--glow-color': `${color}50` }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-all group-hover:scale-110" style={{ background: `${color}1a`, border: `1px solid ${color}33` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <h3 className="text-sm font-bold mb-1.5 dark:text-white text-slate-900">{feat.title}</h3>
                <p className="text-xs dark:text-white/45 text-slate-500 leading-relaxed">{feat.desc}</p>
                <div className="absolute -bottom-12 -right-12 w-28 h-28 rounded-full blur-2xl opacity-0 group-hover:opacity-25 transition-opacity duration-500" style={{ background: color }} />
                <div className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(to right, transparent, ${color}, transparent)` }} />
              </div>
            );
          })}
        </div>
        {t.items.length > 8 && (
          <div className="flex flex-col lg:flex-row justify-center gap-4 sm:gap-5 mt-4 sm:mt-5">
            {t.items.slice(8).map((feat, idx) => {
              const i = idx + 8;
              const Icon = featureIcons[i];
              const color = featureColors[i];
              return (
                <div key={i} data-testid={`feature-hex-tile-${i}`} className="relative group overflow-hidden rounded-2xl p-5 glow-card w-full lg:w-[calc(25%-15px)]" style={{ '--glow-color': `${color}50` }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-all group-hover:scale-110" style={{ background: `${color}1a`, border: `1px solid ${color}33` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <h3 className="text-sm font-bold mb-1.5 dark:text-white text-slate-900">{feat.title}</h3>
                  <p className="text-xs dark:text-white/45 text-slate-500 leading-relaxed">{feat.desc}</p>
                  <div className="absolute -bottom-12 -right-12 w-28 h-28 rounded-full blur-2xl opacity-0 group-hover:opacity-25 transition-opacity duration-500" style={{ background: color }} />
                  <div className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(to right, transparent, ${color}, transparent)` }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
